/**
 * features/hr/PaymentDetails.tsx
 * ------------------------------------------------------------------
 * One home for staff payment/bank details (the account a salary is
 * paid into). Search for an employee, click their row, and a modal
 * lets you set Bank / Account name / Account number / Branch code.
 * A CSV import lets you set them in bulk from a downloaded template.
 *
 * Details are stored on HrPayrollProfile.bank — the same record the
 * payslip and the bank export already read — so this tab feeds those
 * automatically. It replaces the bank fields that used to sit on the
 * Contract tab.
 * ------------------------------------------------------------------
 */
import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Landmark, Search, Download, Upload, Save, Loader2, Pencil } from 'lucide-react';
import { useHrData } from '@/features/hr/useHrData';
import { hr } from '@/features/hr/hrWrite';
import { recordHrAudit } from '@/lib/hrAudit';
import { useAuth } from '@/contexts/AuthContext';
import { staffFullName, type HrStaff } from '@/lib/hr';
import type { BankAccountDetails } from '@/lib/hrPayroll';
import { parseCsv, downloadCsv, makeColFinder, cell } from '@/lib/hrImport';
import { SoftCard, AccentButton, FormModal, glassFieldClass } from '@/components/ui/glass';
import { PageHeader, Spinner } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}
function toCsv(rows: string[][]): string {
  return rows.map((r) => r.map(csvCell).join(',')).join('\n');
}

export default function PaymentDetails({
  onOpenStaff,
}: {
  onOpenStaff?: (id: string) => void;
}) {
  const data = useHrData();
  const { userData } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email ?? null };
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState<HrStaff | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });
  const fileRef = useRef<HTMLInputElement>(null);

  const bankFor = (staffId: string): BankAccountDetails =>
    data.payrollProfiles.find((p) => p.staffId === staffId)?.bank ?? {};

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.staff
      .filter((s) => s.status !== 'exited')
      .filter(
        (s) =>
          !q ||
          `${s.firstName} ${s.lastName} ${s.employeeNo}`
            .toLowerCase()
            .includes(q),
      )
      .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`));
  }, [data.staff, search]);

  const activeStaff = useMemo(
    () => data.staff.filter((s) => s.status === 'active'),
    [data.staff],
  );

  if (data.loading) return <Spinner label="Loading…" />;

  /** Save bank details onto the staff member's payroll profile, preserving
   *  any salary/allowance fields already there (mirrors the old saveBank). */
  async function persistBank(staffId: string, bank: BankAccountDetails) {
    const existing = data.payrollProfiles.find((p) => p.staffId === staffId);
    await hr.payrollProfile.set(staffId, {
      ...(existing ?? {
        staffId,
        basicSalary: 0,
        allowances: [],
        gratuityRatePct: 0,
        gratuityEligible: true,
      }),
      bank,
    });
    await recordHrAudit({
      action: 'profile.bank.update',
      actor,
      subjectId: staffId,
      after: { bank: bank?.bankName, accountName: bank?.accountName },
    });
  }

  function downloadTemplate() {
    const header = [
      'Staff ID',
      'Staff Number',
      'Staff Name',
      'Bank',
      'Account Name',
      'Account Number',
      'Branch Code',
    ];
    const body = activeStaff.map((s) => {
      const b = bankFor(s.id);
      return [
        s.id,
        s.employeeNo ?? '',
        staffFullName(s),
        b.bankName ?? '',
        b.accountName ?? '',
        b.accountNumber ?? '',
        b.branchCode ?? '',
      ];
    });
    downloadCsv('payment-details-template.csv', toCsv([header, ...body]));
  }

  function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => void importCsv(String(reader.result ?? ''));
    reader.readAsText(file);
  }

  async function importCsv(text: string) {
    setBusy(true);
    setSaveModal({ show: true, status: 'saving', message: 'Importing…' });
    try {
      const { headers, rows: dataRows } = parseCsv(text);
      const find = makeColFinder(headers);
      const idCol = find(['Staff ID', 'staffid', 'id']);
      const bankCol = find(['Bank', 'bank name']);
      const nameCol = find(['Account Name', 'accountname']);
      const numCol = find(['Account Number', 'accountnumber', 'account no']);
      const branchCol = find(['Branch Code', 'branchcode', 'branch']);
      let updated = 0;
      let skipped = 0;
      for (const row of dataRows) {
        const sid = cell(row, idCol).trim();
        const staff = data.staffById(sid);
        if (!staff) {
          skipped += 1;
          continue;
        }
        const bank: BankAccountDetails = {
          bankName: cell(row, bankCol).trim() || undefined,
          accountName: cell(row, nameCol).trim() || undefined,
          accountNumber: cell(row, numCol).trim() || undefined,
          branchCode: cell(row, branchCol).trim() || undefined,
        };
        if (!bank.bankName && !bank.accountName && !bank.accountNumber && !bank.branchCode) {
          skipped += 1;
          continue;
        }
        await persistBank(sid, bank);
        updated += 1;
      }
      setSaveModal({
        show: true,
        status: 'success',
        message: `Updated ${updated}${skipped ? `; skipped ${skipped} (blank or unmatched staff)` : ''}.`,
      });
    } catch (err) {
      setSaveModal({
        show: true,
        status: 'error',
        message: (err as Error).message || 'Could not import the file.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payment Details"
        subtitle="The bank account each salary is paid into. Used on payslips and the bank export — update here when details change."
      />

      {/* CSV bulk */}
      <SoftCard hover={false} pad="p-4">
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
          Bulk update by CSV
        </p>
        <p className="text-xs text-gray-500 mb-3">
          The template is pre-filled with current details for all active staff — edit and
          re-upload. Blank rows and unmatched staff are skipped.
        </p>
        <div className="flex items-center gap-2">
          <AccentButton icon={Download} variant="soft" onClick={downloadTemplate}>
            Download template ({activeStaff.length})
          </AccentButton>
          <AccentButton icon={busy ? Loader2 : Upload} disabled={busy} onClick={() => fileRef.current?.click()}>
            {busy ? 'Importing…' : 'Upload CSV'}
          </AccentButton>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleFile}
          />
        </div>
      </SoftCard>

      {/* Search + list */}
      <SoftCard hover={false} pad="p-4">
        <div className="relative mb-3 max-w-sm">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search staff…"
            className={glassFieldClass('py-1.5 text-sm pl-8')}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="py-2 pr-4 font-medium">Staff</th>
                <th className="py-2 px-4 font-medium">Bank</th>
                <th className="py-2 px-4 font-medium">Account number</th>
                <th className="py-2 px-4 font-medium">Branch</th>
                <th className="py-2 pl-4 font-medium text-right">Edit</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => {
                const b = bankFor(s.id);
                return (
                  <tr
                    key={s.id}
                    className="border-b border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    onClick={() => setEditor(s)}
                  >
                    <td className="py-2.5 pr-4">
                      <div className="font-medium text-gray-800 dark:text-gray-100">
                        {staffFullName(s)}
                      </div>
                      <div className="text-xs text-gray-400">{s.employeeNo}</div>
                    </td>
                    <td className="py-2.5 px-4">{b.bankName || <span className="text-amber-500">—</span>}</td>
                    <td className="py-2.5 px-4">
                      {b.accountNumber || <span className="text-amber-500">no account</span>}
                    </td>
                    <td className="py-2.5 px-4">{b.branchCode || '—'}</td>
                    <td className="py-2.5 pl-4 text-right">
                      <span className="inline-flex items-center gap-1 text-gray-500">
                        <Pencil size={13} /> Edit
                      </span>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-sm text-gray-500">
                    No staff match “{search}”.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </SoftCard>

      {editor && (
        <BankEditor
          staff={editor}
          initial={bankFor(editor.id)}
          onSave={persistBank}
          onOpenStaff={onOpenStaff}
          onClose={() => setEditor(null)}
        />
      )}

      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal((m) => ({ ...m, show: false }))}
      />
    </div>
  );
}

function BankEditor({
  staff,
  initial,
  onSave,
  onOpenStaff,
  onClose,
}: {
  staff: HrStaff;
  initial: BankAccountDetails;
  onSave: (staffId: string, bank: BankAccountDetails) => Promise<void>;
  onOpenStaff?: (id: string) => void;
  onClose: () => void;
}) {
  const [bankName, setBankName] = useState(initial.bankName ?? '');
  const [accountName, setAccountName] = useState(initial.accountName ?? '');
  const [accountNumber, setAccountNumber] = useState(initial.accountNumber ?? '');
  const [branchCode, setBranchCode] = useState(initial.branchCode ?? '');
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>(
    { show: false, status: 'saving', message: '' },
  );

  async function save() {
    setSaving(true);
    setModal({ show: true, status: 'saving', message: 'Saving payment details…' });
    try {
      await onSave(staff.id, {
        bankName: bankName.trim() || undefined,
        accountName: accountName.trim() || undefined,
        accountNumber: accountNumber.trim() || undefined,
        branchCode: branchCode.trim() || undefined,
      });
      setModal({ show: true, status: 'success', message: 'Payment details saved.' });
    } catch (e) {
      setModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  const field = (label: string, value: string, set: (v: string) => void) => (
    <label className="block">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{label}</span>
      <input value={value} onChange={(e) => set(e.target.value)} className={glassFieldClass('py-2 mt-1')} />
    </label>
  );

  return (
    <FormModal
      onClose={onClose}
      icon={Landmark}
      title="Payment account"
      subtitle={staffFullName(staff)}
      maxWidth="max-w-lg"
      footer={
        <div className="flex items-center justify-between gap-2">
          {onOpenStaff ? (
            <button
              onClick={() => onOpenStaff(staff.id)}
              className="text-sm text-gray-500 hover:underline"
            >
              Open profile
            </button>
          ) : (
            <span />
          )}
          <AccentButton icon={saving ? Loader2 : Save} disabled={saving} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </AccentButton>
        </div>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {field('Bank', bankName, setBankName)}
        {field('Account name', accountName, setAccountName)}
        {field('Account number', accountNumber, setAccountNumber)}
        {field('Branch code', branchCode, setBranchCode)}
      </div>
      <SaveConfirmation
        show={modal.show}
        status={modal.status}
        message={modal.message}
        onDismiss={() => {
          if (modal.status === 'success') onClose();
          else setModal((m) => ({ ...m, show: false }));
        }}
      />
    </FormModal>
  );
}
